import React, { useState } from 'react';
import { X, AlertCircle, Check, Package } from 'lucide-react';

import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import PostHogService, { BillingEvent } from '../../posthog';
import { useOrganization } from '../../contexts/OrganizationContext';
import { startCheckout } from '../../lib/paddle';

interface BundleContents {
  prs: number;
  sast_scans: number;
  deep_scans: number;
}

interface PurchaseQuotaTopupModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  bundleContents?: BundleContents;
  tierDisplayName?: string;
}

const BUNDLE_PRICE = 99;

export const PurchaseQuotaTopupModal: React.FC<PurchaseQuotaTopupModalProps> = ({
  show,
  onHide,
  onSuccess,
  bundleContents,
  tierDisplayName,
}) => {
  const api = useAuthenticatedApi();
  const { currentOrganization } = useOrganization();
  const [saveCard, setSaveCard] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async () => {
    setLoading(true);
    setError(null);

    try {
      PostHogService.track(BillingEvent.QUOTA_TOPUP_CHECKOUT_CLICKED, {
        organization_id: currentOrganization?.id,
        organization_role: currentOrganization?.current_user_role,
        tier_display_name: tierDisplayName,
        bundle_prs: bundleContents?.prs,
        bundle_sast_scans: bundleContents?.sast_scans,
        bundle_deep_scans: bundleContents?.deep_scans,
        save_payment_method: saveCard,
      });

      const response = await api.post(apiEndpoints.billing.bundleTopup, {
        save_payment_method: saveCard,
      });

      // CityPay redirects away; Paddle opens an overlay and calls onSuccess on completion.
      await startCheckout(api, response, { onComplete: () => onSuccess() });
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!show) return null;

  const hasBundleContents = bundleContents && (
    bundleContents.prs > 0 ||
    bundleContents.sast_scans > 0 ||
    bundleContents.deep_scans > 0
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-ui-background-secondary rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-ui-text-primary">
            Purchase Quota Bundle
          </h3>
          <button
            onClick={onHide}
            className="p-1 hover:bg-ui-background-tertiary rounded-lg transition-colors"
          >
            <X size={20} className="text-ui-text-tertiary" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Bundle Overview */}
          <div className="p-4 bg-brand-primary/5 border border-brand-primary/20 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-brand-primary/10 rounded-lg">
                <Package size={24} className="text-brand-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-ui-text-primary">
                  {tierDisplayName || 'Plan'} Quota Bundle
                </h4>
                <p className="text-sm text-ui-text-secondary">
                  Same quotas as your monthly plan
                </p>
              </div>
            </div>

            <div className="text-3xl font-bold text-ui-text-primary mb-4">
              ${BUNDLE_PRICE}
            </div>

            {hasBundleContents && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wide">
                  Bundle includes:
                </p>
                <ul className="space-y-1.5">
                  {bundleContents.prs > 0 && (
                    <li className="flex items-center justify-between text-sm">
                      <span className="text-ui-text-secondary">Fix PRs</span>
                      <span className="font-medium text-ui-text-primary">+{bundleContents.prs}</span>
                    </li>
                  )}
                  {bundleContents.sast_scans > 0 && (
                    <li className="flex items-center justify-between text-sm">
                      <span className="text-ui-text-secondary">SAST Scans</span>
                      <span className="font-medium text-ui-text-primary">+{bundleContents.sast_scans}</span>
                    </li>
                  )}
                  {bundleContents.deep_scans > 0 && (
                    <li className="flex items-center justify-between text-sm">
                      <span className="text-ui-text-secondary">Deep AI Scans</span>
                      <span className="font-medium text-ui-text-primary">+{bundleContents.deep_scans}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Save Payment Method */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-brand-primary bg-ui-background-secondary border-ui-border-primary rounded focus:ring-brand-primary focus:ring-2"
              />
              <span className="text-sm text-ui-text-secondary group-hover:text-ui-text-primary dark:group-hover:text-white">
                Save payment method for future purchases
              </span>
            </label>
          </div>

          {/* Security Note */}
          <div className="space-y-2 p-4 bg-ui-background-secondary rounded-lg">
            <div className="flex items-center gap-2 text-sm text-ui-text-secondary">
              <Check size={16} className="text-green-500 dark:text-green-400" />
              <span>Secure checkout</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-ui-text-secondary">
              <Check size={16} className="text-green-500 dark:text-green-400" />
              <span>Quotas added immediately after payment</span>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onHide}
            disabled={loading}
            className="px-4 py-2 text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={loading || !hasBundleContents}
            className="px-4 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Spinner size={16} tone="current" />
                Processing...
              </>
            ) : (
              `Pay $${BUNDLE_PRICE}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
